import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { useCurrentUser } from '@/react/hooks/useUser';
import { RegistryValue } from '@/react/kubernetes/helm/components/HelmRegistrySelect';
import { ChartReference } from '@/react/kubernetes/helm/types';
import { useHelmChartList } from '@/react/kubernetes/helm/helmChartSourceQueries/useHelmChartList';

/**
 * Custom hook to check if a chart source (registry or repository) is reachable
 * Uses the useHelmChartList query to determine if charts can be fetched from the source
 * Although this does take a long time, the cache from useHelmChartList can be reused to verify that the chart is valid
 *
 * @param params - Chart source parameters (registryId for OCI, registryUrl for HTTP repos)
 */
export function useIsChartSourceValid(
  chartSource?: ChartReference,
  registryValue?: RegistryValue
) {
  const environmentId = useEnvironmentId();
  const { user } = useCurrentUser();
  const isOCI = !!chartSource?.registryID;

  const chartListQuery = useHelmChartList(
    user.Id,
    environmentId,
    {
      id: chartSource?.registryID,
      url: chartSource?.repoURL,
    },
    isOCI,
    false
  );

  // This case should never happen, but we'll handle it just in case
  if (!registryValue) {
    return {
      isReachable: false,
      isChecking: false,
      hasError: true,
      errorMessage: 'Please select a valid helm registry/repository.',
    };
  }

  const hasNoParams =
    !chartSource || (!chartSource.repoURL && !chartSource.registryID);
  const hasError = hasNoParams || chartListQuery.isError;
  const errorMessage = `Portainer can't reach the Helm ${
    isOCI ? 'registry' : 'repository'
  } '${registryValue.name}'. Please select a valid Helm registry/repository.`;

  return {
    isReachable:
      !hasNoParams && !chartListQuery.isError && !!chartListQuery.data,
    isChecking: chartListQuery.isInitialLoading,
    hasError,
    errorMessage: hasError ? errorMessage : '',
  };
}
